import { IPosition, PositionStatus } from '@models/winnerSelection/ITablePositions';

export type PosInfo = {
    offersCount: number;
    /** Первый победитель — backward-compat. winnerNames содержит всех. */
    winnerName: string | null;
    /** Имена всех победителей (несколько offers с result === "Основной"). */
    winnerNames?: string[];
    hasMainOffer: boolean;
    /* status приходит как с chunk-эндпойнта (batch offers-info),
     * так и через onDataLoaded после загрузки TableDemand активной позиции. */
    status?: PositionStatus;
};

export const STATUS_WEIGHT: Record<PositionStatus, number> = {
    todo: 0,
    'in-progress': 1,
    decided: 2,
};

/** Возвращает финальный список победителей: предпочитаем непустой массив winnerNames,
 *  иначе fallback на winnerName. На выходе всегда массив (возможно пустой). */
export const resolveWinnerNames = (info?: { winnerNames?: string[]; winnerName?: string | null }): string[] => {
    if (!info) return [];
    if (info.winnerNames && info.winnerNames.length > 0) return info.winnerNames;
    if (info.winnerName) return [info.winnerName];
    return [];
};

export const normalizePosInfo = (info: PosInfo): PosInfo => {
    const names = resolveWinnerNames(info);
    if ((info.hasMainOffer || names.length > 0) && info.status !== 'decided') {
        return { ...info, hasMainOffer: true, status: 'decided' };
    }
    return info;
};

/**
 * Защита от регрессии статуса при многократных асинхронных источниках:
 * - chunk /offersInfo приходит в произвольном порядке;
 * - onDataLoaded из TableDemand для активной позиции может прийти раньше chunk'а;
 * - "сильный" статус (decided > in-progress > todo) выигрывает.
 *
 * Known limitation: если победителя реально СНЯЛИ на бэке (decided → in-progress
 * или → todo), то даже свежий chunk с пустым winnerNames не откатит state —
 * мы намеренно сохраняем прежний массив. Это сделано чтобы chunk'и-гонки не
 * стирали уже добытое решение. Для отражения реального снятия победителя нужен
 * явный refetch (например loadData при возврате на страницу) с полным сбросом
 * posInfo. На практике сценарий «победителя сняли» редкий — приоритет отдан
 * стабильности UI при загрузке.
 */
export const mergePosInfo = (current: PosInfo | undefined, incoming: PosInfo): PosInfo => {
    const next = normalizePosInfo(incoming);
    if (!current) return next;

    const prev = normalizePosInfo(current);
    const prevStatus = prev.status ?? 'todo';
    const nextStatus = next.status ?? 'todo';
    const status = STATUS_WEIGHT[prevStatus] > STATUS_WEIGHT[nextStatus] ? prevStatus : nextStatus;

    const prevNames = resolveWinnerNames(prev);
    const nextNames = resolveWinnerNames(next);
    const winnerNames = nextNames.length === 0 ? prevNames : nextNames;
    const winnerName = winnerNames[0] ?? null;

    return {
        offersCount: next.offersCount ?? prev.offersCount,
        winnerName,
        winnerNames,
        hasMainOffer: prev.hasMainOffer || next.hasMainOffer || status === 'decided',
        status,
    };
};

/** Статус позиции: сначала смотрим на серверные поля, затем на cached info. */
export const getStatus = (p: IPosition, info?: PosInfo): PositionStatus => {
    if (p.status) return p.status;
    if (info?.status) return info.status;
    if (p.hasMainOffer || info?.hasMainOffer) return 'decided';
    return 'todo';
};

export const statusLabel = (s: PositionStatus): string => {
    return s === 'decided' ? 'Победитель назначен' : s === 'in-progress' ? 'В работе' : 'Не назначено';
};
